// client/src/components/pages/LexiStar/ReviewQueue/assuranceShared.js
//
// Pure helpers shared by the M3-U1 assurance pages (review queue, recompute
// jobs, commissioning, declarations, trip-detail record panel): who "I" am for
// the two-person rule, error text, the LS-04 §14 confidence bands and factor
// names, review-item vocabulary, queue ordering (age × impact), bulk-triage
// cause groups and correction payloads. No React here — unit-tested directly.

// ── Identity and errors ─────────────────────────────────────────────────────

/**
 * The actor string the server records for the signed-in user
 * (server/modules/lexistar/trips/routes.js actorOf: email, else username, else
 * the numeric id). Used only to explain a disabled two-person action — the
 * database enforces the rule (actor <> second_actor).
 */
export function actorOf(user) {
  if (!user) return null;
  return user.email || user.username || (user.userId != null ? String(user.userId) : null);
}

/** Case-insensitive, whitespace-tolerant actor comparison. */
export function sameActor(a, b) {
  if (a == null || b == null) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

/** The user's candidate identities (email, username, id) — any match means "me". */
export function isMe(user, actor) {
  if (!user || actor == null) return false;
  return [user.email, user.username, user.userId != null ? String(user.userId) : null]
    .filter(Boolean)
    .some((id) => sameActor(id, actor));
}

/** Plain-language error text with the server's message when there is one. */
export function errorMessage(err, fallback = 'Something went wrong. Try again.') {
  const data = err?.response?.data;
  const main = data?.error || data?.message || err?.message || fallback;
  const details = Array.isArray(data?.details) ? data.details.filter(Boolean) : [];
  return details.length ? `${main}: ${details.join('; ')}` : main;
}

// ── Confidence (LS-04 §14) ──────────────────────────────────────────────────

export const REVIEW_THRESHOLD = 70;

/** ≥ 85 high · 70–84 medium · < 70 review. */
export function confidenceBand(confidence) {
  if (confidence === null || confidence === undefined || confidence === '') return null;
  const c = Number(confidence);
  if (!Number.isFinite(c)) return null;
  if (c >= 85) return 'high';
  if (c >= REVIEW_THRESHOLD) return 'medium';
  return 'review';
}

export const BAND_META = {
  high: { label: 'High', className: 'bg-slate-100 text-slate-700', glyph: '●' },
  medium: {
    label: 'Medium',
    className: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-300',
    glyph: '◐',
  },
  review: {
    label: 'Needs review',
    className: 'bg-surface text-red-700 ring-1 ring-inset ring-red-400',
    glyph: '○',
  },
};

/** LS-04 §14 factor names and the rule each applies (default penalties). */
export const FACTOR_META = {
  fix_quality: {
    label: 'Fix quality',
    rule: 'HDOP 2–5: −10; HDOP > 5 or < 5 satellites: −25; unknown-quality feed: −10',
  },
  containment_margin: {
    label: 'Containment margin',
    rule: 'Fix within 15 m of the zone edge: −15; only inside the hysteresis band: −25',
  },
  threshold_margin: {
    label: 'Threshold margin',
    rule: 'Measured value within 10% of the threshold: −15',
  },
  corroboration: {
    label: 'Corroboration',
    rule: 'Corroborated: 0; no second signal: −10; signals disagree: −30',
  },
  sequence_integrity: {
    label: 'Sequence integrity',
    rule: 'Expected predecessor missing (e.g. a departure with no arrival): −25',
  },
  data_continuity: {
    label: 'Data continuity',
    rule: 'Gap over 5 min inside the record: −15; built wholly from back-filled data: −5',
  },
  device_health: {
    label: 'Device health',
    rule: 'Asset flagged degraded for a signal the record uses: −15',
  },
  source_arbitration: {
    label: 'Source arbitration',
    rule: 'Data sources disagree on position by more than 200 m: −20',
  },
};

export const factorLabel = (f) =>
  FACTOR_META[f]?.label ||
  String(f || '')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

/** Normalise a factor list and total it: confidence = max(0, 100 + Σ penalties). */
export function factorBreakdown(factors) {
  const rows = (Array.isArray(factors) ? factors : [])
    .filter((f) => f && f.factor)
    .map((f) => ({
      factor: f.factor,
      label: factorLabel(f.factor),
      penalty: Number(f.penalty) || 0,
      detail: f.detail || '',
      rule: FACTOR_META[f.factor]?.rule || null,
    }))
    .sort((a, b) => a.penalty - b.penalty);
  const total = rows.reduce((s, r) => s + r.penalty, 0);
  return { rows, total, confidence: Math.max(0, 100 + total) };
}

// ── Review items (026 lexistar.review_items) ────────────────────────────────

export const REVIEW_KINDS = {
  low_confidence: { label: 'Low confidence', short: 'Low confidence' },
  registration_unresolved: { label: 'Unresolved registration', short: 'Registration' },
  ticket_match: { label: 'Ticket match to confirm', short: 'Ticket match' },
  reanchor_ambiguous: { label: 'Correction could not be re-anchored', short: 'Re-anchor' },
  unplanned_load: { label: 'Unplanned load detected', short: 'Unplanned load' },
  signed_off_conflict: { label: 'Change to a signed-off trip', short: 'Signed-off conflict' },
};

export const kindLabel = (k) => REVIEW_KINDS[k]?.label || String(k || '').replace(/_/g, ' ');

export const SUBJECT_LABELS = {
  event: 'Event',
  trip: 'Trip',
  ticket_match: 'Ticket match',
  deviation: 'Deviation',
  registration: 'Registration',
  correction: 'Correction',
};

/** Impact weight for the queue order: statements outrank reports outrank nothing. */
export const IMPACT_META = {
  statement: { label: 'Statement', weight: 3, help: 'Excluded from statements until resolved' },
  report: { label: 'Report', weight: 2, help: 'Excluded from reports until resolved' },
  none: { label: 'No impact', weight: 1, help: 'Does not affect statements or reports' },
};

export const REVIEW_STATES = [
  { value: 'open', label: 'Open', states: ['open'] },
  { value: 'resolved', label: 'Resolved', states: ['accepted', 'corrected', 'dismissed'] },
  { value: 'all', label: 'All', states: [] },
];

export const REVIEW_STATE_META = {
  open: {
    label: 'Open',
    glyph: '○',
    className: 'bg-surface text-slate-700 ring-1 ring-inset ring-slate-300',
  },
  accepted: { label: 'Accepted', glyph: '✔', className: 'bg-slate-100 text-slate-700' },
  corrected: { label: 'Corrected', glyph: '✎', className: 'bg-slate-100 text-slate-700' },
  dismissed: { label: 'Dismissed', glyph: '✕', className: 'bg-slate-100 text-slate-500' },
};

const ageHours = (iso, now) => {
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? Math.max(0, (now - t) / 3600000) : 0;
};

/**
 * Age × impact (LS-09 §5.18 "sorted by age and impact"): weight(impact) × (1 + age in
 * hours). A statement item waits a third as long as a no-impact one before it outranks
 * it. Ties go to the older item.
 */
export function priorityScore(item, now = Date.now()) {
  const w = IMPACT_META[item?.impact]?.weight ?? IMPACT_META.report.weight;
  return w * (1 + ageHours(item?.opened_at, now));
}

export function sortReviewQueue(items, now = Date.now()) {
  return [...(items || [])].sort((a, b) => {
    const d = priorityScore(b, now) - priorityScore(a, now);
    if (Math.abs(d) > 1e-9) return d;
    return new Date(a.opened_at) - new Date(b.opened_at);
  });
}

/** "geofence:123:edge" → "Geofence 123 · edge"; "registration:JX12KLGP" → "Registration JX12KLGP". */
export function causeLabel(causeKey) {
  if (!causeKey) return 'No shared cause';
  const parts = String(causeKey).split(':');
  const head = parts[0];
  if (head === 'geofence') {
    return `Geofence ${parts[1] ?? ''}${parts[2] ? ` · ${parts.slice(2).join(' ').replace(/_/g, ' ')}` : ''}`.trim();
  }
  if (head === 'registration') return `Registration ${parts.slice(1).join(':')}`;
  if (head === 'gap') return `Data gap · ${parts.slice(1).join(' ')}`;
  return String(causeKey).replace(/[:_]/g, ' ');
}

/** Open items grouped by cause_key (2 or more share it), most common first. */
export function causeGroups(items) {
  const map = new Map();
  (items || []).forEach((i) => {
    if (!i.cause_key || (i.state && i.state !== 'open')) return;
    const g = map.get(i.cause_key) || { cause_key: i.cause_key, count: 0, kinds: new Set() };
    g.count += 1;
    g.kinds.add(i.kind);
    map.set(i.cause_key, g);
  });
  return [...map.values()]
    .filter((g) => g.count >= 2)
    .map((g) => ({ ...g, kinds: [...g.kinds], label: causeLabel(g.cause_key) }))
    .sort((a, b) => b.count - a.count || a.cause_key.localeCompare(b.cause_key));
}

/** Header stats from GET /review-items: { open, oldest_at, share_this_week }. */
export function readReviewStats(res, items = []) {
  const s = (res && (res.stats || res.header || res.summary)) || res || {};
  const open = s.open ?? s.open_count ?? null;
  return {
    open: open != null ? Number(open) : items.filter((i) => (i.state || 'open') === 'open').length,
    oldest_at:
      s.oldest_at ??
      items
        .filter((i) => (i.state || 'open') === 'open')
        .map((i) => i.opened_at)
        .sort()[0] ??
      null,
    share_this_week: s.share_this_week != null ? Number(s.share_this_week) : null,
    reviewed_this_week: s.opened_this_week ?? s.items_this_week ?? null,
    records_this_week: s.records_this_week ?? s.derived_this_week ?? null,
  };
}

/** A review detail body → { item, positions, subject, factors, relatedEvents, corrections }. */
export function readReviewDetail(res) {
  if (!res || typeof res !== 'object') return null;
  const item = res.item || res.review_item || (res.id ? res : null);
  const ev = res.evidence || res;
  const list = (...keys) => {
    for (const k of keys) if (Array.isArray(ev?.[k])) return ev[k];
    for (const k of keys) if (Array.isArray(res?.[k])) return res[k];
    return [];
  };
  const factors = list('factors');
  return {
    item,
    positions: list('positions', 'track'),
    subject: ev?.subject || res.subject || null,
    factors: factors.length ? factors : item?.factors || [],
    relatedEvents: list('related_events', 'events'),
    corrections: list('corrections'),
    geofence: ev?.geofence || res.geofence || null,
  };
}

// ── Corrections (LS-04 §12 overlay) ─────────────────────────────────────────

export const CORRECTION_KINDS = {
  split: {
    label: 'Split',
    help: 'One record is really two — give the moment it splits.',
  },
  merge: {
    label: 'Merge',
    help: 'This record and another are one — name the other record.',
  },
  void: {
    label: 'Void',
    help: 'It did not happen. The record stays in history but stops counting.',
  },
  reclassify: {
    label: 'Reclassify',
    help: 'It happened, but as something else — give the right type.',
  },
  reassign: {
    label: 'Reassign',
    help: 'It belongs to another trip or truck.',
  },
};

/**
 * Build the `after` payload for POST /review-items/:id:correct, or an error string.
 * fields: { split_at (Date|ISO), merge_with, type, trip_id, asset_key }.
 */
export function buildCorrection(kind, fields = {}) {
  switch (kind) {
    case 'split': {
      const at = fields.split_at instanceof Date ? fields.split_at : new Date(fields.split_at);
      if (!fields.split_at || Number.isNaN(at.getTime())) return { error: 'Give the split time.' };
      return { after: { split_at: at.toISOString() } };
    }
    case 'merge':
      if (!String(fields.merge_with || '').trim())
        return { error: 'Name the record to merge with.' };
      return { after: { merge_with: String(fields.merge_with).trim() } };
    case 'void':
      return { after: { void: true } };
    case 'reclassify':
      if (!String(fields.type || '').trim()) return { error: 'Give the right type.' };
      return { after: { type: String(fields.type).trim().toUpperCase() } };
    case 'reassign': {
      const trip = String(fields.trip_id || '').trim();
      const asset = String(fields.asset_key || '').trim();
      if (!trip && !asset) return { error: 'Give the trip or the truck it belongs to.' };
      const after = {};
      if (trip) after.trip_id = trip;
      if (asset)
        after.asset_key = asset.includes(':')
          ? asset
          : `reg:${asset.replace(/\s+/g, '').toUpperCase()}`;
      return { after };
    }
    default:
      return { error: 'Choose how to correct it.' };
  }
}

// ── Trip record lifecycle (LS-04 §4.1) ──────────────────────────────────────

export const RECORD_STATE_META = {
  OPEN: {
    label: 'Open record',
    glyph: '○',
    className: 'bg-surface text-slate-600 ring-1 ring-inset ring-slate-300',
    help: 'Figures can still change as data arrives.',
  },
  SIGNED_OFF: {
    label: 'Signed off',
    glyph: null, // FaLock in the chip
    className: 'bg-slate-800 text-white',
    help: 'Locked: timeline, tickets, fuel and cost. Changes need an amendment and a second user.',
  },
  AMENDED: {
    label: 'Amended',
    glyph: '✎',
    className: 'bg-surface text-slate-900 ring-2 ring-inset ring-slate-800',
    help: 'Changed after sign-off; every report that includes it carries a watermark.',
  },
};

export const SIGN_OFF_STAGES = ['COMPLETED', 'RECONCILED'];

/** Parse a datetime-local string from the shared picker to ISO (local wall clock). */
export function localToIso(s) {
  if (!s) return null;
  const d = s instanceof Date ? s : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const pad = (n) => String(n).padStart(2, '0');
/** Date → "YYYY-MM-DDTHH:MM" for the shared date-time picker. */
export function toLocalValue(d) {
  if (!d) return '';
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
}

/** Share as a percentage with one decimal under 10 % ("4.2%", "12%"). */
export function fmtShare(ratio) {
  const r = Number(ratio);
  if (ratio === null || ratio === undefined || !Number.isFinite(r)) return '—';
  const pct = r * 100;
  return `${pct < 10 ? pct.toFixed(1) : Math.round(pct)}%`;
}

// ── Keyboard (LS-09 §3: documented shortcuts; ignored while typing) ────────

const REVIEW_KEYS = { a: 'accept', c: 'correct', d: 'dismiss', j: 'next', k: 'previous' };

/** Review-queue shortcut for a keydown, or null (typing, modifiers, inside a dialog). */
export function reviewShortcut(event) {
  if (!event || event.ctrlKey || event.metaKey || event.altKey) return null;
  const t = event.target;
  const tag = t?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable)
    return null;
  if (t?.closest?.('[role="dialog"]')) return null;
  return REVIEW_KEYS[String(event.key).toLowerCase()] || null;
}
